import { observer } from 'mobx-react-lite'
import React, { useState, useEffect } from 'react'
import dayjs from 'dayjs'
import { DayjsFormats } from 'shared/lib/dayjs'
import { Icon, Tooltip, Typography, Button, IconButton } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { isInQuietHoursTimeframe, openQuietHoursSettings } from 'entities/QuietHours'
import { useConversationMessagesContext } from 'widgets/ConversationMessages/context'
import styles from './styles.module.scss'

export const QuietHoursContent = observer(() => {
  const { usersStore, featureFlagsStore } = useEntitiesContext()
  const store = useConversationMessagesContext()
  const user = usersStore.user
  const isRoleMember = !!user?.isMemberRole
  const isRoleAdmin = !!(user?.isOwnerRole || user?.isAdminRole || user?.isManagerRole)
  const quietHours = store.quietHours
  const quietHoursClosureStore = store.quietHoursClosureStore
  const isQuietHoursEnabled = quietHours.isQuietHoursEnabled

  const contactName = store.contact?.first_name || store.contact?.last_name || 'this contact'
  const contactTimezone = store.contact?.timezone

  const [localContactTimeState, setLocalContactTimeState] = useState<{ tz: string; time: string }>({
    tz: '',
    time: '',
  })
  const [isHiddenByUser, setIsHiddenByUser] = useState(false)

  const localContactTime =
    localContactTimeState.tz === contactTimezone && localContactTimeState.time
      ? localContactTimeState.time
      : '--:-- --'

  useEffect(() => {
    setIsHiddenByUser(false)
  }, [store.contact?.id])

  useEffect(() => {
    quietHoursClosureStore.loadClosureCounter()
  }, [quietHoursClosureStore])

  useEffect(() => {
    if (contactTimezone) {
      const updateTime = () => {
        let time
        try {
          time = dayjs.utc().tz(contactTimezone).format(DayjsFormats.time2)
        } catch {
          time = '--:-- --'
        }
        setLocalContactTimeState({ tz: contactTimezone, time })
      }
      updateTime()
      const interval = setInterval(updateTime, 60_000)
      return () => clearInterval(interval)
    }
  }, [contactTimezone])

  const onClose = () => {
    quietHoursClosureStore.incrementClosureCounter()
    setIsHiddenByUser(true)
  }

  if (!featureFlagsStore.quiet_hours || (!isRoleMember && !isRoleAdmin)) return null
  if (
    !isInQuietHoursTimeframe(
      contactTimezone,
      quietHours.quietHoursAllowedTimeFrom,
      quietHours.quietHoursAllowedTimeTo
    )
  )
    return null
  if (!isQuietHoursEnabled && (quietHoursClosureStore.shouldHideBanner || isHiddenByUser))
    return null

  return (
    <div className={styles.wrap}>
      <div className={styles.left}>
        <Tooltip
          label={
            <div className={styles.tooltipContent}>
              <Typography
                variant={'body-md-regular'}
                ariaLabel={'QuietHoursTooltip'}
                color={'var(--content-primary-primary-inverted)'}
              >
                Quiet hours
              </Typography>
              <div className={styles.settingsLink} onClick={openQuietHoursSettings}>
                <Typography
                  variant={'body-md-medium'}
                  ariaLabel={'QuietHoursSettingsLink'}
                  color={'var(--light-blue-50)'}
                >
                  Settings
                </Typography>
              </div>
            </div>
          }
          placement='top'
        >
          <Icon icon='moon' fontSize={16} color={'var(--deep-purple-70)'} />
        </Tooltip>
        <Typography
          variant={'body-md-regular'}
          ariaLabel={'QuietHoursContactInfo'}
          color={'var(--deep-purple-80)'}
        >
          It’s currently {localContactTime} for {contactName}
        </Typography>
      </div>
      {!isQuietHoursEnabled && (
        <div className={styles.right}>
          {isRoleAdmin && (
            <Button
              text={'Set up quiet hours'}
              size={'small'}
              contained={'primary'}
              onClick={openQuietHoursSettings}
            />
          )}
          <IconButton
            ariaLabel={'QuietHoursClose'}
            icon={'close'}
            size={'small'}
            variant={'icon'}
            color={'tertiary'}
            onClick={onClose}
          />
        </div>
      )}
    </div>
  )
})
